import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const AdminContext = createContext(null);
export const AdminAccessProvider = ({ children }) => {
  const [identity, setIdentity] = useState(null);
  const generation = useRef(0);
  const logout = useCallback(() => { generation.current += 1; setIdentity(null); }, []);
  useEffect(() => {
    // Retire the old persisted credential; never restore it into an admin session.
    localStorage.removeItem('parseeker_admin_key');
    sessionStorage.removeItem('parseeker_admin_key');
  }, []);
  const signIn = useCallback(async (email, password) => {
    const attempt = generation.current;
    const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/access/verify`, { email: email.trim(), password });
    if (data.ok !== true) throw new Error('Unable to verify admin access.');
    if (generation.current === attempt) setIdentity({ email: email.trim().toLowerCase(), key: password });
  }, []);
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(response => response, error => {
      const headers = error.config?.headers;
      const supplied = headers?.get?.('X-Admin-Key') || headers?.['X-Admin-Key'];
      const code = error.response?.data?.detail?.code;
      const businessDenial = ['OWNER_CONSENT_REQUIRED', 'COURSE_NOT_AUTHORIZED', 'PREVIEW_FINANCIALS_DISABLED'].includes(code);
      if (identity?.key && supplied === identity.key && [401,403].includes(error.response?.status) && !businessDenial) logout();
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(interceptor);
  }, [identity, logout]);
  const value = useMemo(() => ({ adminKey: identity?.key || '', adminEmail: identity?.email || '', isAuthenticated: Boolean(identity), signIn, logout }), [identity, signIn, logout]);
  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export const useAdminAccess = () => {
  const context = useContext(AdminContext);
  if (!context) throw new Error('AdminAccessProvider is required.');
  return context;
};